import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Alert, Button, Input, Modal, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { KeyOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { baseURL } from '../API/url';

export const MIN_PASSWORD_LENGTH = 8;
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ManagedUserList {
  users: ManagedUser[];
}

export interface ManagedUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  userType: string | null;
  status: string;
  loginEnabled: boolean;
  registeredAt: string | null;
  lastLoginAt: string | null;
}

interface UserManagementModalProps {
  open: boolean;
  onClose: () => void;
  // Email of the signed-in admin; their own login switch is locked so they cannot lock themselves out.
  currentUserEmail?: string;
}

const USER_TYPE_LABELS: Record<string, string> = {
  admin: 'Admin',
  advisor: 'Advisor',
  emd: 'EMD',
};

// Enable/disable login moves a user between these statuses (login requires 'active').
const ACTIVE_STATUS = 'active';
const DISABLED_STATUS = 'pending_review';
const TOGGLEABLE_STATUSES = ['active', 'pending_review', 'pending_confirmation'];

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  pending_review: 'gold',
  pending_confirmation: 'gold',
  declined: 'red',
  expired: 'default',
};

export const formatDateTime = (value: string | null | undefined): string =>
  value && dayjs(value).isValid() ? dayjs(value).format(DATE_TIME_FORMAT) : '';

const fullName = (user: ManagedUser) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ');

const compareText = (a?: string | null, b?: string | null) =>
  (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

const compareDate = (a?: string | null, b?: string | null) =>
  (a ? dayjs(a).valueOf() : 0) - (b ? dayjs(b).valueOf() : 0);

const errorMessage = (error: any, fallback: string) => {
  const detail = error?.response?.data?.message;
  if (Array.isArray(detail)) return detail.join(', ');
  return typeof detail === 'string' && detail ? detail : fallback;
};

const UserManagementModal: React.FC<UserManagementModalProps> = ({
  open,
  onClose,
  currentUserEmail,
}) => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [togglingIds, setTogglingIds] = useState<string[]>([]);

  const [passwordUser, setPasswordUser] = useState<ManagedUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [emailUser, setEmailUser] = useState<ManagedUser | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/usermanagement/users`);
      const data: Partial<ManagedUserList> = response.data || {};
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      console.error('Error loading users:', error);
      message.error(errorMessage(error, 'Unable to load users.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open, loadUsers]);

  const isCurrentUser = useCallback(
    (user: ManagedUser) =>
      !!currentUserEmail && user.email?.toLowerCase() === currentUserEmail.toLowerCase(),
    [currentUserEmail],
  );

  const handleToggleLogin = async (user: ManagedUser, enabled: boolean) => {
    setTogglingIds((ids) => [...ids, user.id]);
    try {
      const response = await axios.put(`${baseURL}/usermanagement/users/${user.id}/login`, {
        enabled,
      });
      const status = response?.data?.status || (enabled ? ACTIVE_STATUS : DISABLED_STATUS);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, loginEnabled: status === ACTIVE_STATUS, status } : u,
        ),
      );
      message.success(`Login ${enabled ? 'enabled' : 'disabled'} for ${user.email}.`);
    } catch (error) {
      console.error('Error updating login access:', error);
      message.error(errorMessage(error, 'Unable to update login access.'));
    } finally {
      setTogglingIds((ids) => ids.filter((id) => id !== user.id));
    }
  };

  const openPasswordModal = (user: ManagedUser) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const closePasswordModal = () => {
    setPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleSavePassword = async () => {
    if (!passwordUser) return;
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.put(`${baseURL}/usermanagement/users/${passwordUser.id}/password`, {
        password: newPassword,
      });
      message.success(`Password updated for ${passwordUser.email}.`);
      closePasswordModal();
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(errorMessage(error, 'Unable to change the password.'));
    } finally {
      setSavingPassword(false);
    }
  };

  const openEmailModal = (user: ManagedUser) => {
    setEmailUser(user);
    setNewEmail('');
    setEmailError('');
  };

  const closeEmailModal = () => {
    setEmailUser(null);
    setNewEmail('');
    setEmailError('');
  };

  const handleSaveEmail = async () => {
    if (!emailUser) return;
    // Login lower-cases what the user types, so the stored address is lower-cased too.
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (email === (emailUser.email || '').toLowerCase()) {
      setEmailError('The new email is the same as the current email.');
      return;
    }

    setSavingEmail(true);
    try {
      const response = await axios.put(`${baseURL}/usermanagement/users/${emailUser.id}/email`, {
        email,
      });
      const savedEmail = response?.data?.email || email;
      setUsers((prev) =>
        prev.map((u) => (u.id === emailUser.id ? { ...u, email: savedEmail } : u)),
      );
      message.success(`Login email changed from ${emailUser.email} to ${savedEmail}.`);
      closeEmailModal();
    } catch (error) {
      console.error('Error changing login email:', error);
      setEmailError(errorMessage(error, 'Unable to change the login email.'));
    } finally {
      setSavingEmail(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      [user.email, fullName(user), user.userType ? USER_TYPE_LABELS[user.userType] || user.userType : '']
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [users, searchText]);

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: ManagedUser, b: ManagedUser) => compareText(a.email, b.email),
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'Name',
      key: 'name',
      render: (user: ManagedUser) => fullName(user),
      sorter: (a: ManagedUser, b: ManagedUser) => compareText(fullName(a), fullName(b)),
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (userType: string | null) => (userType ? USER_TYPE_LABELS[userType] || userType : ''),
      sorter: (a: ManagedUser, b: ManagedUser) => compareText(a.userType, b.userType),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>{(status || '').replace(/_/g, ' ')}</Tag>
      ),
      sorter: (a: ManagedUser, b: ManagedUser) => compareText(a.status, b.status),
    },
    {
      title: 'Register Date',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (value: string | null) => formatDateTime(value),
      sorter: (a: ManagedUser, b: ManagedUser) => compareDate(a.registeredAt, b.registeredAt),
    },
    {
      title: 'Latest Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (value: string | null) =>
        value ? formatDateTime(value) : <Typography.Text type="secondary">Never</Typography.Text>,
      sorter: (a: ManagedUser, b: ManagedUser) => compareDate(a.lastLoginAt, b.lastLoginAt),
    },
    {
      title: 'Login',
      key: 'loginEnabled',
      align: 'center' as const,
      render: (user: ManagedUser) => {
        const self = isCurrentUser(user);
        const toggleable = TOGGLEABLE_STATUSES.includes(user.status);
        const lockReason = !toggleable
          ? `Login cannot be changed for a ${(user.status || '').replace(/_/g, ' ')} user`
          : self && user.loginEnabled
            ? 'You cannot disable your own login'
            : '';
        const toggle = (
          <Switch
            checked={user.loginEnabled}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
            loading={togglingIds.includes(user.id)}
            disabled={!!lockReason}
            onChange={(checked) => handleToggleLogin(user, checked)}
            aria-label={`Login access for ${user.email}`}
          />
        );
        return lockReason ? <Tooltip title={lockReason}>{toggle}</Tooltip> : toggle;
      },
      sorter: (a: ManagedUser, b: ManagedUser) => Number(a.loginEnabled) - Number(b.loginEnabled),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center' as const,
      render: (user: ManagedUser) => (
        <Space size="small">
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => openPasswordModal(user)}
            aria-label={`Change password for ${user.email}`}
          >
            Change Password
          </Button>
          <Button
            size="small"
            icon={<MailOutlined />}
            onClick={() => openEmailModal(user)}
            aria-label={`Change login email for ${user.email}`}
          >
            Change Login Email
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <span>
            User Management
            <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
              {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
            </span>
          </span>
        }
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={1350}
      >
        <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
          <Input.Search
            placeholder="Search by email, name or user type"
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 360 }}
          />
          <Button icon={<ReloadOutlined />} onClick={loadUsers} loading={loading}>
            Refresh
          </Button>
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          size="small"
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          scroll={{ y: 420 }}
        />
      </Modal>

      <Modal
        title={passwordUser ? `Change Password: ${passwordUser.email}` : 'Change Password'}
        open={!!passwordUser}
        onCancel={closePasswordModal}
        onOk={handleSavePassword}
        okText="Save Password"
        confirmLoading={savingPassword}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Password
            placeholder={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordError('');
            }}
            autoComplete="new-password"
            aria-label="New password"
          />
          <Input.Password
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setPasswordError('');
            }}
            onPressEnter={handleSavePassword}
            autoComplete="new-password"
            aria-label="Confirm new password"
          />
          {passwordError && <Typography.Text type="danger">{passwordError}</Typography.Text>}
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            The user will be signed out of any active session and must log in with the new password.
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title="Change Login Email"
        open={!!emailUser}
        onCancel={closeEmailModal}
        onOk={handleSaveEmail}
        okText="Save Email"
        confirmLoading={savingEmail}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text strong>Current email</Typography.Text>
          <Input value={emailUser?.email || ''} disabled aria-label="Current email" />
          <Typography.Text strong>New email</Typography.Text>
          <Input
            type="email"
            placeholder="Enter the new login email"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setEmailError('');
            }}
            onPressEnter={handleSaveEmail}
            autoComplete="off"
            aria-label="New email"
          />
          {emailError && <Typography.Text type="danger">{emailError}</Typography.Text>}
          {emailUser && (emailUser.userType === 'advisor' || emailUser.userType === 'emd') && (
            <Alert
              type="warning"
              showIcon
              message={
                emailUser.userType === 'advisor'
                  ? "Advisor portal data is matched by email. Make sure the advisor's Salesforce contact email is changed to the same address."
                  : "EMD portal data is matched by the EMD firm's contact email. Make sure it is changed to the same address."
              }
            />
          )}
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            The user will be signed out and must log in with the new email.
          </Typography.Text>
        </Space>
      </Modal>
    </>
  );
};

export default UserManagementModal;
